import React, { useRef, useEffect } from "react";
import gsap from "gsap";

const Home: React.FC = () => {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      /* Set initial state immediately — before any delay */
      gsap.set(".hw1", { yPercent: 110, opacity: 0 });
      gsap.set(".hw2", { yPercent: 110, opacity: 0 });
      gsap.set(".hero-desc-1, .hero-desc-2", { y: "2em", opacity: 0 });
      gsap.set(".hero-scroll-ind", { opacity: 0 });

      const tl = gsap.timeline({ delay: 0.9 });

      tl.to(
        ".hw1",
        {
          yPercent: 0,
          opacity: 1,
          duration: 1,
          stagger: 0.09,
          ease: "power3.out",
        },
        0,
      );

      tl.to(
        ".hw2",
        {
          yPercent: 0,
          opacity: 1,
          duration: 1,
          stagger: 0.09,
          ease: "power3.out",
        },
        0.32,
      );

      tl.to(
        ".hero-desc-1, .hero-desc-2",
        {
          y: 0,
          opacity: 1,
          duration: 0.8,
          stagger: 0.18,
          ease: "power2.out",
        },
        0.55,
      );

      tl.to(
        ".hero-scroll-ind",
        {
          opacity: 1,
          duration: 0.6,
          ease: "power2.out",
        },
        1.05,
      );
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <section
      id="home"
      ref={sectionRef}
      className="relative min-h-96 bg-[#f6f4ef] overflow-hidden"
    >
      {/* ── Content ── */}
      <div className="relative z-10 flex min-h-screen flex-col justify-start px-6 pb-24 pt-32 sm:px-8 sm:pt-36 md:px-14 md:pt-40 lg:px-20">
        {/* ── Row 1: name heading (left) + desc 1 (top-right) ── */}
        <div className="flex flex-wrap items-start justify-between gap-y-6">
          {/* Name headline — each word is overflow-hidden so the slide-up is clipped */}
          <h1
            className="m-0 max-w-full p-0 uppercase leading-[0.9]"
            style={{
              fontFamily: "'League Gothic', sans-serif",
              fontSize: "clamp(3.2rem, 10vw, 12rem)",
            }}
          >
            {["Hi!", "I'm", "Ahmed."].map((word) => (
              <span
                key={word}
                className="inline-block overflow-hidden align-bottom"
                style={{ marginRight: "0.12em" }}
              >
                <span className="hw1 inline-block bg-gradient-to-b from-[#181818] to-[#7a746a] bg-clip-text text-transparent">
                  {word}
                </span>
              </span>
            ))}
          </h1>

          {/* Description 1 */}
          <p
            className="hero-desc-1 text-sm md:text-base text-[#5f5b53]
                        leading-relaxed max-w-[250px] mt-2 shrink-0 self-start"
          >
            I started learning web development in 2023 and began contributing
            to real client and Wix app projects in 2024.
          </p>
        </div>

        {/* ── Row 2: desc 2 (bottom-left) + role heading (right/bottom) ── */}
        <div className="mt-16 flex flex-col-reverse items-start justify-between gap-8 lg:mt-20 lg:flex-row lg:items-end">
          {/* Description 2 */}
          <p
            className="hero-desc-2 max-w-[260px] shrink-0 text-sm leading-relaxed text-[#5f5b53] md:text-base lg:self-end"
          >
            I've worked on ambitious products at Ichonic — including 3
            apps on the Wix App Market — and I enjoy building accessible,
            human-centered interfaces.
          </p>

          {/* Role headline */}
          <h2
            className="m-0 max-w-full p-0 uppercase leading-[0.88]"
            style={{
              fontFamily: "'League Gothic', sans-serif",
              fontSize: "clamp(3.2rem, 15vw, 10rem)",
            }}
          >
            {["Full", "Stack", "Developer"].map((word) => (
              <span
                key={word}
                className="inline-block max-w-full overflow-hidden align-bottom max-[520px]:block"
                style={{ marginRight: "0.1em" }}
              >
                <span className="hw2 inline-block bg-gradient-to-b from-[#181818] to-[#7a746a] bg-clip-text text-transparent">
                  {word}
                </span>
              </span>
            ))}
          </h2>
        </div>

        {/* ── Scroll indicator: bottom-center ── */}
        <a
          href="#work"
          className="hero-scroll-ind absolute bottom-2 left-1/2 -translate-x-1/2
                     flex flex-col items-center gap-3 group margin-top: 8rem"
        >
          <span
            className="text-[12px] uppercase tracking-[0.22em] text-[#181818]
                           group-hover:text-[#7a746a] transition-colors duration-300"
          >
            see my work
          </span>
          <span className="relative w-px h-14 bg-[#d9d3c8] overflow-hidden">
            <span
              className="absolute top-0 left-0 w-full h-full bg-[#181818]
                             animate-scroll-line"
            />
          </span>
        </a>
      </div>
    </section>
  );
};

export default Home;
